import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import {bool, number, oneOf, shape, string} from 'prop-types'
import localeData from '../helpers/login.lang'
import {language, loginModes, loginModesList} from '../helpers/login.data'
import {
  checkIfAuthNotificationIsVisible,
  didPasswordUpdateFail,
  didPasswordUpdateFailRepeatedly,
  isErrorMessage,
  wasTFACodeSent
} from './welcomeSection.helper'

import Header from './header/header.presenter'
import Instruction from './instruction/instruction.presenter'
import Notification from './notification/notification.presenter'
import {extractInstanceProps} from '../login.helper'

const {
  createNewPassword,
  forgotPassword,
  newUserLinkExpired,
  forgottenPasswordLinkExpired
} = loginModes

const locale = localeData[language]

const WelcomeSection = ({instanceProperties, hasAuthFailed, authFailedTimes, codeSendMessage, mode, TFAChannel, isPasswordDirty, isConfirmPasswordDirty, isPasswordPolicyPending, auditSubmitFailed, isFirstTime}) => {
  const {environment} = extractInstanceProps(instanceProperties)
  return (
    <Fragment>
      <Header
        instanceProperties={instanceProperties}
      />

      <Instruction
        isVisible={mode === loginModes.forgotPasswordEmailSent}
        caption={locale.checkYourEmailTitle()}
        info={locale.youCanCloseThisPage()}
        uniqueID={mode}
      />

      <Notification
        isVisible={isErrorMessage({codeSendMessage})}
        type='error'
      >
        {locale.codeSendFailedMessage(codeSendMessage)}
      </Notification>

      <Notification
        isVisible={checkIfAuthNotificationIsVisible({mode, hasAuthFailed, authFailedTimes})}
        type='error'
      >
        {locale.authFailed()}
      </Notification>

      <Instruction
        isVisible={wasTFACodeSent({mode, codeSendMessage})}
        caption={locale.tfaCodeTitle()}
        info={locale.secretCodeSent(TFAChannel)}
        uniqueID={mode}
      />

      <Instruction
        isVisible={mode === forgotPassword}
        caption={locale.forgotPasswordTitle()}
        info={locale.forgotPasswordInfo({environment})}
        uniqueID={mode}
      />

      <Instruction
        isVisible={mode === createNewPassword}
        caption={isFirstTime && locale.welcomeToAgillic()}
        info={isFirstTime ? locale.createYourPassword() : locale.renewYourPassword()}
        uniqueID={mode}
      />

      <Instruction
        isVisible={mode === newUserLinkExpired}
        caption={locale.linkExpiredTitle()}
        info={locale.linkExpiredInfo()}
        uniqueID={mode}
      />

      <Instruction
        isVisible={mode === forgottenPasswordLinkExpired}
        caption={locale.forgottenPasswordLinkExpiredTitle()}
        info={locale.forgottenPasswordLinkExpiredInfo()}
        uniqueID={mode}
      />

      <Notification
        isVisible={didPasswordUpdateFail({
          mode,
          isPasswordDirty,
          hasAuthFailed,
          authFailedTimes,
          isConfirmPasswordDirty
        })}
        type='error'
      >
        {locale.updatePasswordFailed()}
      </Notification>

      <Notification
        isVisible={didPasswordUpdateFailRepeatedly({isPasswordPolicyPending, authFailedTimes})}
        type='error'
      >
        <p>{locale.contactAdministrator()}</p>
        <p>{instanceProperties.troubleshootingInfo}</p>
        <p>{locale.ifNoOneHasAccess()}</p>
      </Notification>

      <Instruction
        isVisible={mode === loginModes.audit}
        caption={locale.auditLabel()}
        info={locale.auditInfo()}
        uniqueID={mode}
      />

      <Notification
        isVisible={auditSubmitFailed}
        type='error'
      >
        <p>{locale.submitAuditFailed()}</p>
        <p>{locale.contactAdministrator()}</p>
        <p>{instanceProperties.troubleshootingInfo}</p>
      </Notification>
    </Fragment>
  )
}

const mapStateToProps = ({loginReducer}) => ({
  instanceProperties: loginReducer.instanceProperties,
  hasAuthFailed: loginReducer.hasAuthFailed,
  authFailedTimes: loginReducer.authFailedTimes,
  isFirstTime: loginReducer.isFirstTime,
  codeSendMessage: loginReducer.codeSendMessage,
  codeSendDelay: loginReducer.codeSendDelay,
  mode: loginReducer.mode,
  TFAChannel: loginReducer.TFAChannel,
  isPasswordDirty: loginReducer.isPasswordDirty,
  isConfirmPasswordDirty: loginReducer.isConfirmPasswordDirty,
  isPasswordPolicyPending: loginReducer.isPasswordPolicyPending,
  canRequestNewCode: loginReducer.canRequestNewCode,
  auditSubmitFailed: loginReducer.auditSubmitFailed
})

const mapDispatchToProps = () => ({})

WelcomeSection.propTypes = {
  instanceProperties: shape({
    name: string,
    type: string
  }),
  hasAuthFailed: bool,
  authFailedTimes: number,
  codeSendMessage: string,
  mode: oneOf(loginModesList),
  TFAChannel: string,
  isPasswordDirty: bool,
  isConfirmPasswordDirty: bool,
  isPasswordPolicyPending: bool,
  auditSubmitFailed: bool,
  isFirstTime: bool
}

export default connect(mapStateToProps, mapDispatchToProps)(WelcomeSection)
